import { showDeleteConfirm } from '../dom/confirmModal';

interface DeleteActionConfig {
  checkboxSelector?: string;
  deleteButtonId?: string;
  formName?: string;
  confirmMessage?: string;
  alertMessage?: string;
  enableRowClick?: boolean;
  rowSelector?: string;
}

class DeleteActionHandler {
  private checkboxSelector: string;
  private deleteButtonId: string;
  private formName: string;
  private confirmMessage: string;
  private enableRowClick: boolean;
  private rowSelector: string;
  private checkboxes: NodeListOf<HTMLInputElement>;
  private deleteButton: HTMLElement | null;
  private form: HTMLFormElement | null;

  constructor(config: DeleteActionConfig = {}) {
    this.checkboxSelector = config.checkboxSelector ?? 'input[name="it[]"]';
    this.deleteButtonId = config.deleteButtonId ?? 'deleteButton';
    this.formName = config.formName ?? 'list-form';
    this.confirmMessage = config.confirmMessage ?? 'Are you sure you want to delete the selected items? This action cannot be undone.';
    this.enableRowClick = config.enableRowClick ?? true;
    this.rowSelector = config.rowSelector ?? '.table-selectable tbody tr';
    this.checkboxes = document.querySelectorAll(this.checkboxSelector);
    this.deleteButton = document.getElementById(this.deleteButtonId);
    this.form = document.querySelector<HTMLFormElement>(`form[name="${this.formName}"]`);

    this.init();
  }

  private init(): void {
    if (!this.deleteButton || this.checkboxes.length === 0) {
      return;
    }

    this.setupEventListeners();
    this.updateDeleteButtonVisibility();
  }

  private setupEventListeners(): void {
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateDeleteButtonVisibility());
    });

    if (this.deleteButton) {
      this.deleteButton.addEventListener('click', (e) => void this.handleDelete(e));
    }

    if (this.enableRowClick) {
      this.setupRowClickHandlers();
    }
  }

  private setupRowClickHandlers(): void {
    const rows = document.querySelectorAll<HTMLTableRowElement>(this.rowSelector);

    rows.forEach(row => {

      row.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;

        if (
          target.tagName === 'A' ||
          target.tagName === 'BUTTON' ||
          target.tagName === 'INPUT' ||
          target.closest('a') ||
          target.closest('button')
        ) {
          return;
        }

        const checkbox = row.querySelector<HTMLInputElement>(this.checkboxSelector);
        if (checkbox) {
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change'));
        }
      });
    });
  }

  private updateDeleteButtonVisibility(): void {
    if (!this.deleteButton) return;

    const checkedBoxes = document.querySelectorAll<HTMLInputElement>(
      `${this.checkboxSelector}:checked`,
    );

    // Always ensure button is visible
    this.deleteButton.classList.remove('d-none');

    if (checkedBoxes.length > 0) {
      this.deleteButton.removeAttribute('disabled');
    } else {
      this.deleteButton.setAttribute('disabled', 'disabled');
    }
  }

  private async handleDelete(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.form) {
      return;
    }

    const confirmed = await showDeleteConfirm(this.confirmMessage);

    if (confirmed) {
      this.form.submit();
    }
  }
}

export function initDeleteActionHandler(config?: DeleteActionConfig): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new DeleteActionHandler(config);
    });
  } else {
    new DeleteActionHandler(config);
  }
}

export default DeleteActionHandler;
